// The face the page is set in (the Mac's Settings ▸ Appearance ▸ Font, `EditorController.face`, `BookFonts.faces`):
// Newsreader, the book's own, or one of a few alternatives — Literata, Georgia, Palatino, Poppins, Atkinson Hyperlegible,
// Courier Prime — kept in this browser as `data-face` on the document (app.css maps it to `--page-face`, which the
// editor's `--ws-serif` reads), under the Mac's UserDefaults name. Scripts stay in Courier Prime; the chrome, the Revise
// reader and the reading views keep Newsreader.
import { useSyncExternalStore } from 'react';

export type Face = 'newsreader' | 'literata' | 'georgia' | 'palatino' | 'poppins' | 'atkinson' | 'courier-prime';

export const FACES: { value: Face; label: string; note: string; kind: 'serif' | 'sans' | 'typewriter' }[] = [
  { value: 'newsreader', label: 'Newsreader', note: 'The book’s own serif, drawn for reading at length.', kind: 'serif' },
  { value: 'literata', label: 'Literata', note: 'A serif made for reading on screens, with small capitals of its own.', kind: 'serif' },
  { value: 'georgia', label: 'Georgia', note: 'The familiar screen serif.', kind: 'serif' },
  { value: 'palatino', label: 'Palatino', note: 'A classic book face.', kind: 'serif' },
  { value: 'poppins', label: 'Poppins', note: 'A round, geometric sans.', kind: 'sans' },
  { value: 'atkinson', label: 'Atkinson Hyperlegible', note: 'Drawn for low vision: every letter and figure distinct.', kind: 'sans' },
  { value: 'courier-prime', label: 'Courier Prime', note: 'The typewriter face scripts are set in.', kind: 'typewriter' },
];

const KEY = 'ws:editorFace';
const listeners = new Set<() => void>();

function stored(): Face {
  try {
    const value = localStorage.getItem(KEY);
    return FACES.some((f) => f.value === value) ? (value as Face) : 'newsreader';
  } catch {
    return 'newsreader';
  }
}

/** Puts the choice on the document (at start-up, and whenever it changes). */
export function applyFace(face: Face = stored()) {
  if (face === 'newsreader') delete document.documentElement.dataset.face;
  else document.documentElement.dataset.face = face;
}

export function setFace(face: Face) {
  try {
    if (face === 'newsreader') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, face);
  } catch {
    // kept for this page only
  }
  applyFace(face);
  for (const fn of listeners) fn();
}

// Chosen in another tab of this browser (Settings open beside the writing): the browser's `storage` event, which never
// fires in the tab that wrote.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== KEY) return;
    applyFace();
    for (const fn of listeners) fn();
  });
}

/** The face chosen in this browser. */
export function useFace(): Face {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    stored,
    () => 'newsreader' as const,
  );
}
